import React, { useEffect, useMemo, useState } from 'react';
import { Button, Container, Header, Icon, Label, Segment } from 'semantic-ui-react';
import { Navigate } from 'react-router-dom';
import GenericTable from '../common/GenericTable';
import { useAuth } from '../context/AuthContext';
import { stockyApi } from '../misc/StockyApi';

function FeatureFlagsDashboard() {
  const Auth = useAuth();
  const isLoggedIn = Auth.userIsAuthenticated();
  const isAdmin = Auth.isAdmin();
  const accessToken = Auth.user?.accessToken || Auth.getUser()?.accessToken || '';
  const userAuthenticated = useMemo(
    () => (accessToken ? { accessToken } : null),
    [accessToken],
  );

  const [flags, setFlags] = useState([]);
  const [loadingFlags, setLoadingFlags] = useState(false);
  const [selectedFeatureKey, setSelectedFeatureKey] = useState('');
  const [featureUsers, setFeatureUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [busyUsername, setBusyUsername] = useState('');

  const fetchFlags = async () => {
    if (!userAuthenticated) return;
    try {
      setLoadingFlags(true);
      const response = await stockyApi.getFeatureFlags(userAuthenticated);
      setFlags(Array.isArray(response?.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching feature flags:', error);
    } finally {
      setLoadingFlags(false);
    }
  };

  const fetchFeatureUsers = async (featureKey) => {
    if (!userAuthenticated || !featureKey) return;
    try {
      setLoadingUsers(true);
      const response = await stockyApi.getFeatureFlagUsers(userAuthenticated, featureKey);
      setFeatureUsers(Array.isArray(response?.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching feature flag users:', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  useEffect(() => {
    if (!isLoggedIn || !isAdmin || !userAuthenticated) return;
    fetchFlags();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, isAdmin, userAuthenticated]);

  useEffect(() => {
    if (!selectedFeatureKey) {
      setFeatureUsers([]);
      return;
    }
    fetchFeatureUsers(selectedFeatureKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFeatureKey]);

  const editPrice = async (flag) => {
    const raw = window.prompt(
      `Precio mensual para "${flag.label}"`,
      String(flag.price ?? 0),
    );
    if (raw === null) return;
    const value = Number(raw);
    if (!Number.isFinite(value) || value < 0) {
      window.alert('Ingresa un precio valido mayor o igual a 0.');
      return;
    }
    try {
      await stockyApi.updateFeatureFlag(userAuthenticated, flag.featureKey, { price: value });
      await fetchFlags();
    } catch (error) {
      console.error('Error updating feature flag price:', error);
      window.alert('No se pudo actualizar el precio.');
    }
  };

  const editLabel = async (flag) => {
    const raw = window.prompt('Nombre del modulo', flag.label || '');
    if (raw === null) return;
    const trimmed = raw.trim();
    if (!trimmed) {
      window.alert('El nombre no puede estar vacio.');
      return;
    }
    try {
      await stockyApi.updateFeatureFlag(userAuthenticated, flag.featureKey, { label: trimmed });
      await fetchFlags();
    } catch (error) {
      console.error('Error updating feature flag label:', error);
      window.alert('No se pudo actualizar el nombre.');
    }
  };

  const toggleUserFeature = async (row) => {
    const nextEnabled = !(row.enabled === true);
    const selectedFlag = flags.find((flag) => flag.featureKey === selectedFeatureKey);
    if (nextEnabled && selectedFlag && Number(selectedFlag.price) > 0) {
      const confirmed = window.confirm(
        `Vas a activar "${selectedFlag.label}" para ${row.username}. Se le van a sumar $${Number(selectedFlag.price).toLocaleString('es-AR', { minimumFractionDigits: 2 })} al monto a pagar el mes siguiente. Continuar?`,
      );
      if (!confirmed) return;
    }
    try {
      setBusyUsername(row.username);
      await stockyApi.setUserFeatureFlag(userAuthenticated, selectedFeatureKey, row.username, nextEnabled);
      setFeatureUsers((prev) =>
        prev.map((user) => (user.username === row.username ? { ...user, enabled: nextEnabled } : user)),
      );
      await fetchFlags();
    } catch (error) {
      console.error('Error toggling user feature flag:', error);
      window.alert('No se pudo actualizar el modulo para este usuario.');
    } finally {
      setBusyUsername('');
    }
  };

  if (!isLoggedIn || !isAdmin) {
    return <Navigate to="/home" />;
  }

  const flagsColumnsConfig = [
    { key: 'label', label: 'Modulo' },
    { key: 'featureKey', label: 'Clave' },
    {
      key: 'price',
      label: 'Precio mensual',
      render: (value) => `$${Number(value || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}`,
    },
    { key: 'enabledCount', label: 'Usuarios activos' },
  ];

  const flagsActions = [
    {
      icon: 'edit',
      color: 'grey',
      title: 'Editar nombre',
      onClick: (row) => editLabel(row),
    },
    {
      icon: 'dollar',
      color: 'blue',
      title: 'Editar precio',
      onClick: (row) => editPrice(row),
    },
    {
      icon: 'users',
      color: 'teal',
      title: 'Ver usuarios',
      onClick: (row) => setSelectedFeatureKey(row.featureKey),
    },
  ];

  const usersColumnsConfig = [
    { key: 'username', label: 'Usuario' },
    { key: 'email', label: 'Email' },
    {
      key: 'enabled',
      label: 'Estado',
      render: (value) => <Label color={value ? 'green' : undefined}>{value ? 'ON' : 'OFF'}</Label>,
    },
  ];

  const usersActions = [
    {
      icon: 'toggle on',
      color: 'green',
      title: 'Activar/desactivar',
      disabled: (row) => busyUsername === row.username || loadingUsers,
      onClick: (row) => toggleUserFeature(row),
    },
  ];

  const selectedFlag = flags.find((flag) => flag.featureKey === selectedFeatureKey);

  return (
    <Container fluid style={{ padding: 20 }}>
      <Header as="h2" style={{ margin: 0, marginBottom: 12 }}>
        Feature flags
      </Header>
      <GenericTable
        columnsConfig={flagsColumnsConfig}
        data={flags}
        showCheckbox={false}
        actions={flagsActions}
        rowClickable={false}
        emptyMessage={loadingFlags ? 'Cargando...' : 'Sin feature flags'}
      />

      {selectedFeatureKey ? (
        <Segment style={{ marginTop: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <Header as="h3" style={{ margin: 0 }}>
              Usuarios - {selectedFlag?.label || selectedFeatureKey}
            </Header>
            <Button basic onClick={() => setSelectedFeatureKey('')}>
              <Icon name="close" />
              Cerrar
            </Button>
          </div>
          <GenericTable
            columnsConfig={usersColumnsConfig}
            data={featureUsers}
            showCheckbox={false}
            actions={usersActions}
            rowClickable={false}
            emptyMessage={loadingUsers ? 'Cargando...' : 'Sin usuarios'}
          />
        </Segment>
      ) : null}
    </Container>
  );
}

export default FeatureFlagsDashboard;
